import { removeMdnFormatting, sanitizeCountryCode } from "@expert/common-ui";
import { useCallIntroFlowQuestions } from "@expert/contentful";
import { useGaiaWebsocket } from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { Button, Checkbox, Container, Flex, InputBase, Stack, Text } from "@mantine/core";
import { isEmail, useForm } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { AnalyticsProvider } from "@soluto-private/eventualize-react";
import {
    addSessionSummaryMessage,
    deactivatedSummaryMessage,
    deactivatePreviousSessionSummaryMessage,
    useTimelineStore,
} from "@soluto-private/expert-workspace-timeline";
import { useQuery } from "@tanstack/react-query";
import { type JSX, useEffect, useState } from "react";
import { IMaskInput } from "react-imask";
import { getRootDispatcher, useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import { useCallDetailsStore } from "@/call-details";
import { useCustomerProgram, useVerifiedCustomerStore } from "@/customer-info";
import { type SessionSummary, useIVRInfo } from "@/customer-info/api";
import { getCustomerDetails } from "@/customer-info/api/customerDetails";
import { useAutoSelectSurvey } from "@/customer-info/hooks/useAutoSelectSurvey";
import {
    MAX_MDN_REASON,
    maskToRegex,
    sanitizeGaiaSessionContextProperties,
    shouldShowCallIntroFlowQuestions,
} from "@/customer-panel/components/customer-verification/CustomerVerificationUpdated.helpers";
import { isBlocklisted } from "@/customer-panel/components/customer-verification/utils/isBlocklisted";
import { useGetPartnerPrograms, usePartnerConfig } from "@/partner/usePartnerConfig";
import { useProductOfferStore } from "@/sales-tools";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { LoadingAnimation } from "@/sales-tools/home-product/components/Icons";
import { useRapportBuildingStore } from "@/sales-tools/rapport-building/useRapportBuildingStore";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { getHashedValue, removeCountryCode, validatePhoneNumber } from "@/shared-utils";
import type { Customer, CustomerDetailsPayload } from "../../../customer-info";
import { BillingAccountNumberAndPinSection } from "./BillingAccountNumberAndPinSection";
import { CustomerNamesSection } from "./CustomerNamesSection";
import { CustomerProgramSelection } from "./CustomerProgramSelection";
import classes from "./CustomerVerificationUpdated.module.css";
import { useIsConfirmationDisabled } from "./hooks/useIsConfirmationDisabled";
import { IVRHandoff } from "./IVRHandoff";
import { SimplifiedMdnSection } from "./SimplifiedMdnSection";
import { useVerificationFlowStore } from "./store";
import { determineCustomerVerificationStatus } from "./utils/determineCustomerVerificationStatus";
import { env } from "./utils/environment";
import { hasIVRInfo } from "./utils/isIVRHandoffStep";

const NO_SUMMARY_AVAILABLE_MESSAGE = "No summary available from past 30 days.";

export function CustomerVerificationFlowV3({
    mdnFromTask,
    isPreviousSessionSummaryEnabled,
}: {
    mdnFromTask: string;
    isPreviousSessionSummaryEnabled: boolean;
}): JSX.Element {
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const { dispatcher } = useAnalytics();
    const { task: mostRecentTask, isVoice, isChat, voiceTask } = useTypedMostRecentTask();
    const sessionId = useSessionId();
    const [currentSubscriptions, setCurrentSubscriptions] = useState<string[]>();
    const { customer, isEditingVerificationInput } = useVerifiedCustomerStore((state) =>
        state.getVerifiedCustomer(sessionId),
    );

    const unverifiedCustomerData = useVerifiedCustomerStore((state) => state.getUnverifiedCustomerData(sessionId));
    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const currentStep = stepHistory.at(stepHistory.length - 1);

    const { setUnverifiedCustomerData, setIsEditingVerificationInput } = useVerifiedCustomerStore.getState();
    const debouncedSetUnverifiedCustomerData = useDebouncedCallback(
        (sessionId: string, customerData: Partial<Customer>) => {
            setUnverifiedCustomerData(sessionId, customerData);
        },
        { delay: 300, flushOnUnmount: true },
    );

    const latestCustomerData = { ...customer, ...unverifiedCustomerData };

    const mdn = latestCustomerData?.mdn ?? mdnFromTask;
    const customerName = latestCustomerData?.fullName;

    const {
        features: {
            programSelectionEnabled,
            verificationType,
            isInternationalPhoneEnabled = false,
            defaultCountry = "US",
        },
        partnerToolUrl,
        additionalVerificationFields,
        blocklistedNumbers,
        blocklistedPrefixes,
        survey,
    } = usePartnerConfig();
    const partner = usePartner();
    const callIntroQuestions = useCallIntroFlowQuestions(partner);
    const callDetails = useCallDetailsStore.getState().callDetails;
    const programs = useGetPartnerPrograms();
    const { customerProgram } = useCustomerProgram();
    const useCustomerAgreementProgram = survey?.useCustomerAgreementProgram ?? false;
    const showProgramSelection =
        !isChat && programSelectionEnabled && programs.length > 1 && !useCustomerAgreementProgram;
    const showAdditionalDetailsEmailInput = additionalVerificationFields?.includes("email") ?? false;
    const showAdditionalDetailsOrderIdInput = additionalVerificationFields?.includes("orderId") ?? false;
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const hasProducts = products.length > 0;
    const showAdditionalDetailsPhoneNumberInput = verificationType !== "phoneNumber" && hasProducts;
    const {
        intent,
        summary,
        device,
        customerName: customerNameFromIVR,
        orderNumber: orderNumberFromIVR,
        productLink,
    } = useIVRInfo();
    const { orderIdMask } = usePartnerConfig();

    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);

    useAutoSelectSurvey();

    if (!mostRecentTask || (!isVoice && !isChat)) {
        throw new Error("Active task is not a supported task type (voice or chat)");
    }

    // TODO: Add mdn and name capture to the mantine useForm rather than managing it manually
    const verificationForm = useForm<{
        billingAccountNumber: string;
        pin: string;
    }>({
        validateInputOnBlur: true,
        initialValues: {
            billingAccountNumber: latestCustomerData?.accountNumber ?? "",
            pin: "",
        },
        validate: {
            billingAccountNumber: (value) => {
                if (verificationType === "billingAccountNumberAndPin") {
                    // TODO: Follow up regarding what the exact validation/mask should be for billing account number
                    if (!/^(?:\d{13}|vze\d{10})$/.test(value)) {
                        return "Billing account number is required";
                    }
                }
                return null;
            },
            pin: (value) => {
                if (verificationType === "billingAccountNumberAndPin" && value.length > 0) {
                    if (!/^\d+$/.test(value)) {
                        return "PIN can only contain numbers";
                    }
                }
                return null;
            },
        },
    });

    const additionalDetailsForm = useForm<{
        email: string;
        orderId: string;
        mdn: string;
    }>({
        validateInputOnBlur: true,
        initialValues: {
            email: latestCustomerData?.email ?? "",
            orderId: latestCustomerData?.orderId ?? orderNumberFromIVR ?? "",
            mdn: latestCustomerData?.mdn ?? mdnFromTask,
        },
        validate: {
            email: (value) => {
                if (showAdditionalDetailsEmailInput) {
                    return isEmail("Invalid email")(value);
                }
                return null;
            },
            orderId: (value) => {
                if (showAdditionalDetailsOrderIdInput && value.length > 0) {
                    const validationRegex = orderIdMask ? maskToRegex(orderIdMask) : /^\d{3}-\d{7}-\d{7}$/;
                    if (!validationRegex.test(value)) {
                        return "Invalid order ID";
                    }
                }
                return null;
            },
            mdn: (value) => {
                if (showAdditionalDetailsPhoneNumberInput) {
                    if (isBlocklisted(value, blocklistedNumbers, blocklistedPrefixes)) {
                        void dispatcher.dispatchBusinessEvent("BlocklistedMdnSubmitted");

                        return "This isn’t the customer’s phone number. Please ask for the right one.";
                    }

                    if (!validatePhoneNumber(value).valid) {
                        return `Invalid phone number`;
                    }
                }
                return null;
            },
        },
    });

    additionalDetailsForm.watch("email", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { email: value });
        }
    });

    additionalDetailsForm.watch("orderId", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { orderId: value });
        }
    });

    additionalDetailsForm.watch("mdn", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { mdn: removeMdnFormatting(value) });
        }
    });

    const rootDispatcher = getRootDispatcher();

    const lastPrevSummaryMessage = useTimelineStore()
        .getTimelineState(sessionId)
        .messages.findLast((msg) => msg.type === "SessionSummary");

    const deactivateLastSummary = (prevSummary?: SessionSummary) => {
        const lastSummary = lastPrevSummaryMessage?.metadata?.sessionSummary;
        const shouldHideLastPrevSummaryMessage =
            lastSummary && lastPrevSummaryMessage?.text !== deactivatedSummaryMessage;

        if (shouldHideLastPrevSummaryMessage) {
            void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryHidden", {
                hiddenReason: "mdnUpdate",
                newSummaryExists: !!prevSummary,
            });
            deactivatePreviousSessionSummaryMessage(sessionId, lastSummary.timestamp);
        }
    };

    /** Caution: timelineStore updates to add and deactivate session summary MUST occur in the same event loop */
    const addPrevSummaryToTimeline = (prevSummary?: SessionSummary) => {
        if (!isPreviousSessionSummaryEnabled || isChat) return;

        deactivateLastSummary(prevSummary);

        const header = "Previous session";
        const sessionType = "previous";

        if (!prevSummary) {
            addSessionSummaryMessage(sessionId, {
                header,
                summary: NO_SUMMARY_AVAILABLE_MESSAGE,
                sessionType,
                timestamp: new Date().toString(),
            });
            return;
        }

        const { summary, timestamp, resolutionStatus } = prevSummary;
        void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryShown", {
            previousSummaryTimestamp: timestamp,
        });
        trackFullStoryEvent("PreviousSessionSummaryShown", { previousSummaryTimestamp: timestamp });
        addSessionSummaryMessage(sessionId, {
            header,
            summary,
            timestamp,
            resolutionStatus,
            sessionType,
        });
    };

    const customerDetailsCallback = (result: CustomerDetailsPayload) => {
        // Only update the email input if the field is currently empty and the result has a primary account holder email
        if (
            showAdditionalDetailsEmailInput &&
            additionalDetailsForm.getValues().email === "" &&
            result?.primaryAccountHolder?.email != null
        ) {
            additionalDetailsForm.setFieldValue("email", result?.primaryAccountHolder?.email);
        }

        addPrevSummaryToTimeline(result.previousSummary);
    };

    const { data: mdnLookupData, isLoading: isCustomerDetailsByMDNLoading } = useQuery({
        queryKey: [`customer-details-${mdn}`, mdn],
        queryFn: async () =>
            getCustomerDetails({
                mdn: mdn ?? "",
                partner,
                searchType: "phoneNumber",
                dispatcher,
                customerDetailsCallback,
                modelResultsMdn: mdn,
            }),
        enabled: verificationType === "phoneNumber" && validatePhoneNumber(mdn).valid,
        refetchOnMount: true,
    });

    const {
        data: banLookupData,
        isLoading: isCustomerDetailsByBANLoading,
        refetch: fetchBanLookupData,
    } = useQuery({
        queryKey: [
            `customer-details-${verificationForm.getValues().billingAccountNumber}-${verificationForm.getValues().pin}`,
            verificationForm.getValues().billingAccountNumber + verificationForm.getValues().pin,
        ],
        queryFn: async () =>
            getCustomerDetails({
                billingAccountNumber: verificationForm.getValues().billingAccountNumber,
                pin: verificationForm.getValues().pin.length > 0 ? verificationForm.getValues().pin : undefined,
                partner,
                searchType: "billingAccountNumberAndPin",
                dispatcher,
                customerDetailsCallback,
                modelResultsMdn: mdn,
            }),
        enabled: false,
        refetchOnMount: true,
    });

    const customerData = verificationType === "phoneNumber" ? mdnLookupData : banLookupData;

    const isCustomerDetailsLoading = isCustomerDetailsByMDNLoading || isCustomerDetailsByBANLoading;

    const gaiaWS = useGaiaWebsocket();

    useEffect(() => {
        // if expert is coming back to change mdn/name, reset all the fields.
        // once there is a singular flow, we need to make this file less complex
        if (stepHistory.includes("VerificationComplete")) {
            setIsEditingVerificationInput(sessionId, true);
            setUnverifiedCustomerData(sessionId, { fullName: undefined });
        }
    }, [sessionId, setIsEditingVerificationInput, setUnverifiedCustomerData, stepHistory]);

    const isEditingCustomerInformation = stepHistory.includes("VerificationComplete");

    const onConfirmClick = async () => {
        if (
            !customerName ||
            (verificationType === "phoneNumber" && !mdn) ||
            !additionalDetailsForm.isValid() ||
            !gaiaWS
        )
            return;
        debouncedSetUnverifiedCustomerData.flush();
        const { setCustomer } = useVerifiedCustomerStore.getState();
        const { pushStep } = useVerificationFlowStore.getState();
        const { billingAccountNumber } = verificationForm.getValues();
        const {
            email: additionalDetailsEmail,
            orderId: additionalDetailsOrderId,
            mdn: additionalDetailsMdn,
        } = additionalDetailsForm.getValues();
        if (customerData?.previousDevices) {
            useDetectedCuesStore.getState().processPreviouslyDetectedCues(sessionId, customerData.previousDevices);
            useRapportBuildingStore.getState().updateLocation({
                city: customerData.primaryAccountHolder?.address?.City,
                state: customerData.primaryAccountHolder?.address?.StateCode,
                timezone: customerData.primaryAccountHolder?.address?.TimeZone?.Name,
            });
        }
        const confirmedMdn = verificationType === "phoneNumber" ? mdn : additionalDetailsMdn;
        const confirmedEmail = showAdditionalDetailsEmailInput
            ? additionalDetailsEmail
            : customerData?.primaryAccountHolder?.email;
        const confirmedOrderId =
            showAdditionalDetailsOrderIdInput && additionalDetailsOrderId.length > 0
                ? additionalDetailsOrderId
                : undefined;
        const confirmedAccountNumber =
            verificationType === "billingAccountNumberAndPin"
                ? billingAccountNumber
                : customerData?.primaryAccountHolder?.accountNumber;

        setCustomer(
            sessionId,
            {
                ...customerData?.primaryAccountHolder,
                currentSubscriptions,
                fullName: customerName,
                email: confirmedEmail,
                mdn: confirmedMdn,
                orderId: confirmedOrderId,
                accountNumber: confirmedAccountNumber,
            },
            customerData?.deviceActivation,
        );
        const callDetails = useCallDetailsStore.getState().callDetails;
        const formattedMdn = removeCountryCode(confirmedMdn);

        const properties = [
            { field: "hashed_mdn", value: await getHashedValue(formattedMdn), action: "update" },
            { field: "mdn", value: formattedMdn, action: "update" },
            { field: "country_code", value: validatePhoneNumber(confirmedMdn).countryCode, action: "update" },
            { field: "client_channel_id", value: customerData?.clientChannelId, action: "update" },
        ];

        if (customerData?.previousDevices && customerData.previousDevices.length > 0) {
            properties.push({
                field: "previous_devices",
                value: customerData.previousDevices.join(","),
                action: "add",
            });
        }

        void gaiaWS.sendJsonMessage({
            action: "update-session-context",
            correlationId: mostRecentTask?.sessionId,
            sessionId: isVoice && voiceTask ? voiceTask.agentCallId : mostRecentTask?.sessionId,
            sessionGroupId: mostRecentTask?.sessionId,
            partner,
            context: {
                properties: sanitizeGaiaSessionContextProperties(properties),
            },
        });

        void dispatcher
            .withMeta({ Client: partner, SkipMixpanel: true, SkipOpenSearch: true })
            .withExtra({
                planId: customerData?.primaryAccountHolder?.planId,
                RetailName: customerData?.primaryAccountHolder?.productName,
                AccountType: callDetails.ACCOUNT_TYPE,
                AccountCategory: callDetails.ACCOUNT_CATEGORY,
                EnrollmentStatus: callDetails.VALIDATION_FLAG,
                numPreviousDevices: customerData?.previousDevices?.length ?? 0,
                currentSubscriptions,
            })
            .withIdentities({
                MDN: formattedMdn,
                Email: confirmedEmail,
                AccountNumber: confirmedAccountNumber,
            })
            .dispatchBusinessEvent("SupportedProfileDisplayed");
        void dispatcher
            .withMeta({ SkipMixpanel: true, SkipEDP: true, isTest: true, SkipOpenSearch: true })
            .withExtra({
                confirmedMdn,
                mdnFromTask,
            })
            .dispatchBusinessEvent("MdnConfirmedByExpert");
        if (!env.isDevMode && partnerToolUrl) {
            const authorizedUsers = Array.from(customerData?.authorizedUsers ?? []);
            if (customerData?.primaryAccountHolder) authorizedUsers.push(customerData.primaryAccountHolder);

            if (partner === "verizon") {
                window.open(
                    `${partnerToolUrl}${determineCustomerVerificationStatus(
                        customerName,
                        formattedMdn,
                        authorizedUsers,
                    )}&mdnSearched=${formattedMdn}`,
                    "_blank",
                    "noopener,noreferrer",
                );
            } else {
                window.open(partnerToolUrl, "_blank", "noopener,noreferrer");
            }
        }
        pushStep(sessionId, "VerificationComplete");
    };
    const compareName = (): string => {
        if (customerData?.primaryAccountHolder?.fullName === undefined) {
            return "NoCustomerFound";
        } else if (
            customerData.primaryAccountHolder.fullName === customerName ||
            customerData.authorizedUsers?.find((user) => user.fullName === customerName)
        ) {
            return "ExistingCustomerNameSelected";
        }
        return "NewCustomerNameAdded";
    };

    const saveBillingAccountNumberAndPin = () => {
        const didBanOrPinChange = verificationForm.isDirty("billingAccountNumber") || verificationForm.isDirty("pin");
        setUnverifiedCustomerData(sessionId, {
            accountNumber: verificationForm.getValues().billingAccountNumber,
            ...(didBanOrPinChange ? { fullName: undefined } : {}),
        });
        setIsEditingVerificationInput(sessionId, false);
        void dispatcher.dispatchBusinessEvent("BAN_Submitted");
        void fetchBanLookupData();
    };

    const customerNamesSectionKey = () => {
        let keyBase = "";
        if (verificationType === "phoneNumber") {
            keyBase = mdn;
        } else if (verificationType === "billingAccountNumberAndPin") {
            keyBase = verificationForm.getValues().billingAccountNumber + verificationForm.getValues().pin;
        }
        // Ensure key is always unique by including sessionId and a component-specific prefix
        return `names-section-${sessionId}-${keyBase}`;
    };

    const { isConfirmationDisabled, reason: reasonConfirmationIsDisabled } = useIsConfirmationDisabled({
        additionalDetailsFormIsValid: additionalDetailsForm.isValid(),
        customerName,
        customerProgram,
        isEditingVerificationInput,
        mdn,
        sessionId,
        showProgramSelection,
        verificationFormIsValid: verificationForm.isValid(),
        verificationType,
    });

    return (
        <AnalyticsProvider
            dispatcher={(dispatch) =>
                dispatch.withExtra({
                    newFlow: true,
                    page: currentStep,
                })
            }
        >
            {currentStep === "CustomerPhoneInput" && (
                <Flex id="customer-confirmation-flexbox" className={classes.root}>
                    {hasIVRInfo({ intent, summary, device, productLink }) && !isEditingCustomerInformation && (
                        <IVRHandoff intent={intent} summary={summary} device={device} productLink={productLink} />
                    )}
                    {verificationType === "phoneNumber" && (
                        <SimplifiedMdnSection
                            key={`mdn-section-${sessionId}-${mdn}`}
                            initialMdn={mdn}
                            isEditing={isEditingVerificationInput}
                            shouldAutoFocus={mdn === "" && isEditingVerificationInput && !isChat}
                            onComplete={(input) => {
                                setUnverifiedCustomerData(sessionId, {
                                    mdn: input,
                                    ...(input !== mdn ? { fullName: undefined } : {}),
                                });
                                setIsEditingVerificationInput(sessionId, false);
                            }}
                            onEditClick={() => setIsEditingVerificationInput(sessionId, true)}
                            isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                            defaultCountry={sanitizedDefaultCountry}
                        />
                    )}
                    {verificationType === "billingAccountNumberAndPin" && (
                        <BillingAccountNumberAndPinSection
                            form={verificationForm}
                            isEditing={isEditingVerificationInput}
                            onEditClick={() => setIsEditingVerificationInput(sessionId, true)}
                            onSaveClick={saveBillingAccountNumberAndPin}
                        />
                    )}
                    {showAdditionalDetailsEmailInput && (
                        <Stack gap={0} w="100%">
                            <InputBase
                                data-testid="email-input"
                                classNames={{ input: `${classes.styledInput} fs-mask`, label: classes.styledLabel }}
                                style={{ display: isCustomerDetailsLoading ? "none" : undefined }}
                                mt="xxl"
                                size="sm"
                                c="dark.1"
                                label="What’s their email?"
                                key={`email-input-${additionalDetailsForm.key("email")}`}
                                {...additionalDetailsForm.getInputProps("email")}
                            />
                        </Stack>
                    )}
                    {!isCustomerDetailsLoading ? (
                        <>
                            <CustomerNamesSection
                                key={customerNamesSectionKey()}
                                customer={customerData ?? undefined}
                                customerNameFromIVR={customerNameFromIVR}
                                onOptionSelect={(input) => {
                                    debouncedSetUnverifiedCustomerData(sessionId, { fullName: input });
                                }}
                                isDisabled={
                                    verificationType === "phoneNumber" ? mdn === "" : isEditingVerificationInput
                                }
                            />
                            {showProgramSelection && <CustomerProgramSelection />}
                            <Stack gap={0} w="100%">
                                {showAdditionalDetailsPhoneNumberInput && (
                                    <InputBase
                                        data-testid="additional-details-phone-number-input"
                                        classNames={{
                                            input: `${classes.styledInput} fs-mask`,
                                            label: classes.styledLabel,
                                        }}
                                        mt="xxl"
                                        size="sm"
                                        c="dark.1"
                                        label="What’s their phone number?"
                                        component={IMaskInput}
                                        mask="+1 (000) 000-0000"
                                        key={`phone-input-${additionalDetailsForm.key("mdn")}`}
                                        {...additionalDetailsForm.getInputProps("mdn")}
                                        // The below code is to fix the issue with the IMaskInput using the onAccept event rather than onChange. This was causing validation to not trigger in certain scenarios.
                                        onChange={undefined}
                                        onAccept={(value) =>
                                            additionalDetailsForm.setFieldValue("mdn", removeMdnFormatting(value))
                                        }
                                    />
                                )}
                                {showAdditionalDetailsOrderIdInput && (
                                    <InputBase
                                        data-testid="order-id-input"
                                        classNames={{
                                            input: `${classes.styledInput} fs-mask`,
                                            label: classes.styledLabel,
                                        }}
                                        mt="xxl"
                                        size="sm"
                                        c="dark.1"
                                        label="What’s their order ID?"
                                        component={IMaskInput}
                                        mask={orderIdMask}
                                        key={`order-input-${additionalDetailsForm.key("orderId")}`}
                                        {...additionalDetailsForm.getInputProps("orderId")}
                                        // The below code is to fix the issue with the IMaskInput using the onAccept event rather than onChange. This was causing validation to not trigger in certain scenarios.
                                        onChange={undefined}
                                        onAccept={(value) => additionalDetailsForm.setFieldValue("orderId", value)}
                                    />
                                )}
                            </Stack>
                        </>
                    ) : (
                        <Flex className={classes.animationRoot}>
                            <Container maw="160px">
                                <LoadingAnimation />
                            </Container>
                        </Flex>
                    )}

                    {callIntroQuestions && shouldShowCallIntroFlowQuestions(partner, callDetails) && (
                        <Checkbox.Group
                            mt="1rem"
                            value={currentSubscriptions}
                            onChange={setCurrentSubscriptions}
                            label={callIntroQuestions.question}
                        >
                            <Stack mt="xs">
                                {callIntroQuestions.options.map((option) => (
                                    <Checkbox key={option} value={option} label={option} />
                                ))}
                            </Stack>
                        </Checkbox.Group>
                    )}

                    <Stack mt="24px" gap={"xs"}>
                        {reasonConfirmationIsDisabled === MAX_MDN_REASON && (
                            <Text size={"sm"} c={"error.5"}>
                                You’ve reached the phone number edit limit, but you can still use a previous number.
                            </Text>
                        )}

                        <ButtonWithAnalytics
                            size="sm"
                            fz="xs"
                            analytics={{ actionId: "Confirm", extraContext: { customerNameSelected: compareName() } }}
                            onClick={() => {
                                void onConfirmClick();
                            }}
                            disabled={isConfirmationDisabled}
                            id="change-phone-number-button"
                            data-testid="change-phone-number-button"
                            title={reasonConfirmationIsDisabled}
                        >
                            Confirm
                        </ButtonWithAnalytics>
                    </Stack>
                </Flex>
            )}
        </AnalyticsProvider>
    );
}
